
import { AttendeeRequest, ScheduleRequest } from './schedule_request';
import { ScheduleVisibility } from './visibility';
import { Resource } from './resource';
import { ILeaveUseRequest } from './leave';

export interface ICalendarTransformer<T> {
  transform(
    calendarId: string,
    event: any,
    recurringEvent: T | null
  ): CalendarScheduleSyncRequest;

  transformBack(
    scheduleId: string,
    eventSource: ICalendarEvent,
    calendar: ICalendar
  ): T;
}

export enum CalendarSource {
  Google = 'google',
  Microsoft = 'microsoft'
}

export enum CalendarType {
  Resource = 'resource',
  User = 'user'
}

export type CalendarCreateOrUpdateRequest = {
  organization_id: string;
  type: CalendarType;
  reference_id: string;
  source: CalendarSource;
  integration_id: string;
  is_enabled: boolean;
  is_pull_enabled: boolean;
  is_push_enabled: boolean;
  sync_events: boolean;
  sync_leaves: boolean;
};

export interface CalendarSyncRequest extends CalendarCreateOrUpdateRequest {
  time_zone: string | null;
  location: string | null;
  summary: string | null;
  description: string | null;
}

export interface ICalendar {
  id: string;
  organization_id: string;
  type: CalendarType;
  reference_id: string;
  resource: Resource | null;
  source: CalendarSource;
  integration_id: string;
  time_zone: string | null;
  location: string | null;
  summary: string | null;
  description: string | null;
  is_enabled: boolean;
  is_pull_enabled: boolean;
  is_push_enabled: boolean;
  sync_events: boolean;
  sync_leaves: boolean;
  last_event_updated: string | null;
  last_synced: string | null;
  created: string;
}

export type CalendarIntegrationSyncRequest = {
  schedule_id: string | null;
  event_id: string;
  event_updated: string;
  ical_id: string;
  url: string | null;
  event: any; // This could either be a Google or Microsoft event
  is_deleted: boolean;
  resource_attendees: AttendeeRequest[] | null;
};

export type CalendarEventRequest = {
  creator: AttendeeRequest;
  organizer: AttendeeRequest;
  attendees: AttendeeRequest[];
  start: string;
  original_start: string | null;
  end: string;
  time_zone: string;
  recurring: ScheduleRequest['recurring'] | null;
  title: string | null;
  description: string | null;
  visibility: ScheduleVisibility;
};

export type CalendarScheduleRequest = CalendarEventRequest & {
  integration: CalendarIntegrationSyncRequest;
};

export type CalendarScheduleSyncRequest = {
  recurring_schedule: CalendarScheduleRequest | null;
  schedule: CalendarScheduleRequest;
};

export type CalendarEventSyncRequest = CalendarEventRequest & {
  schedule_id: string;
  event_id: string | null;
  is_deleted: boolean;
  resource_attendees: AttendeeRequest[] | null;
};

export type ResGetCalendars = {
  elements: ICalendar[];
  cursor: string;
  total: number;
};

export interface ICalendarEvent {
  is_deleted: boolean;
  calendar_event_id: string | null;
  calendar_integration_id: string | null;
  calendar_event_integration_id: string | null;
  start_time: string;
  end_time: string;
  organizers: AttendeeRequest[];
  attendees: AttendeeRequest[];
  resource_attendees: AttendeeRequest[];
  title: string | null;
  description: string | null;
  location: string | null;
  locations: string[] | null;
  visibility: ScheduleVisibility;
  recurring_rules: string[] | null;
  recurring_end_time: string | null;
  time_zone_id: string;
}

export interface ICalendarPendingEvent {
  job_id: string | null;
  calendar: ICalendar | null;
}

export interface IPendingCalendarEvent {
  job_id: string | null;
  organization_id: string;
  schedule_id: string | null;
  event: ICalendarEvent | null;
}

export type CalendarLeaveIntegrationSyncRequest = {
  event_id: string | null;
  event_updated: string;
  ical_id: string;
  url: string | null;
  event: any;
  is_deleted: boolean;
};

export type CalendarLeaveSyncRequest = {
  integration: CalendarLeaveIntegrationSyncRequest;
  use: ILeaveUseRequest;
};

export interface ICalendarSyncJob {
  id: string;
  organization_id: string;
  calendar: ICalendar;
  start_time: string | null;
  end_time: string | null;
  is_full_sync: boolean;
  failures: number;
  maximum_failures: number | null;
  sync_token: string | null;
  last_page_token: string | null;
  status: 'pending' | 'idle' | 'syncing' | 'failing' | 'failed' | 'finished';
  sync_seconds: number;
  sync_update_minutes: number;
  weight: number;
  created: string;
  updated: string;
  started: string | null;
  failed: string | null;
  synced: string | null;
  finished: string | null;
  notified: string | null;
  last_notified: string | null;
  last_notified_by_listener_id: string | null;
}

export type CalendarCreateSyncJobRequest = {
  start_time: string | null;
  end_time: string | null;
  is_full_sync: boolean;
  maximum_failures: number | null;
  sync_token: string | null;
  sync_seconds: number;
  check_existing: boolean;
};

export type ResGetCalendarSyncJobs = {
  elements: ICalendarSyncJob[];
  cursor: string;
  total: number;
};

export interface IPendingCalendarSyncJob {
  job: ICalendarSyncJob | null;
  has_job: boolean;
}

export type CalendarAuthorizationCreateOrUpdateRequest = {
  source: CalendarSource;
  identifier: string;
  is_government: boolean;
  is_parallelizable: boolean;
  maximum_failures: number | null;
};

export interface ICalendarAuthorization {
  id: string;
  organization_id: string;
  source: CalendarSource;
  identifier: string;
  is_government: boolean;
  is_parallelizable: boolean;
  failures: number;
  maximum_failures: number | null;
  status: 'idle' | 'syncing' | 'failing' | 'failed';
  created: string;
  updated: string;
  requested: string | null;
  synced: string | null;
  failed: string | null;
}

export interface ICalendarAuthorizationForSyncing {
  authorization: ICalendarAuthorization | null;
  has_authorization: boolean;
}

export interface ResHasAuthorization {
  has_authorization: boolean;
}

export type CalendarAuthorizationIntentCreateOrUpdateRequest = {
  source: CalendarSource;
  authorizations_minimum: number;
  check_secs: number;
  checks_maximum: number | null;
};

export interface ICalendarAuthorizationIntent {
  id: string;
  organization_id: string;
  source: CalendarSource;
  status: 'failed' | 'finished' | 'idle' | 'pending' | 'syncing';
  authorizations: number;
  authorizations_minimum: number;
  check_secs: number;
  checks: number;
  checks_maximum: number;
  last_checked: string | null;
  synced: string | null;
  failed: string | null;
  first_authorization_id: string | null;
  last_authorization_id: string | null;
  intent_updated: string;
  created: string;
  updated: string;
}

export interface ICalendarAuthorizationIntentForSyncing {
  intent: ICalendarAuthorizationIntent | null;
  has_intent: boolean;
}

export interface ICalendarListener {
  id: string;
  organization_id: string;
  calendar: ICalendar;
  created_by_authorization_id: string | null;
  listener_uri: string;
  token: string;
  integration_id: string | null;
  integration_uri: string | null;
  status: 'expired' | 'idle' | 'pending' | 'stopped' | 'syncing';
  expiration_time: string;
  synced: string | null;
  created: string;
  updated: string;
}

export type CalendarListenerListeningResponse = {
  listener: ICalendarListener | null;
};

export type CalendarListenerCreateOrUpdateRequest = {
  listener_uri: string;
  expiration_time: string;
  time_zone_id: string;
  integration_id: string | null;
  integration_uri: string | null;
};

export type CalendarMarkListenerAsStartedRequest = {
  authorization_id: string;
  token: string;
  expiration_time: string;
  time_zone_id: string;
  integration_id: string;
  integration_uri: string;
};

export interface ICalendarSyncCheck {
  calendar: ICalendar | null;
  has_calendar: boolean;
}

export interface IEventSyncRequest {
  organization_id: string;
  calendar_id: string;
  calendar_event_id: string;
  request: CalendarScheduleSyncRequest;
}

export interface IPendingEventSyncRequest {
  event_request: IEventSyncRequest | null;
  has_event_request: boolean;
}
